/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import BaseEditor, { FieldName, ActionComposerEditorArgs } from 'cio/components/action-composer/base-glimmer/editor';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { union } from '@ember/object/computed';
import afterRender from 'cio/utils/after-render';
import { task } from 'ember-concurrency';
import ApiService from 'cio/services/api';
import { tracked } from '@glimmer/tracking';
import { isNone } from '@ember/utils';
import { getDefaultRecipient } from 'cio/utils/composer/slack';
import TemplateModel from 'cio/models/template';
import { getLiquidToInsert } from 'cio/utils/liquid';

export default class SlackEditor extends BaseEditor {
  @service api!: ApiService;

  @tracked channels: string[] = [];
  @tracked users: string[] = [];

  @union('channels', 'users') channelsAndUsernames!: string[];

  container: HTMLElement | null = null;

  @action
  didInsert(element: HTMLElement) {
    this.container = element;
  }

  constructor(owner: unknown, args: ActionComposerEditorArgs) {
    super(owner, args);

    if (!this.args.hasReadOnlyPermission) {
      this.getChannelsAndUsers.perform();
    }
  }

  @computed('args.editableEntity.template.id')
  get template() {
    return this.args.editableEntity.template as TemplateModel;
  }

  @computed('args.editableEntity.campaign.isWebhookTriggered')
  get defaultRecipient() {
    return getDefaultRecipient(this.args.editableEntity.campaign);
  }

  get isDefaultRecipient() {
    if (this.args.editableEntity.campaign?.isWebhookTriggered) {
      return this.template.recipient === this.defaultRecipient;
    }

    return this.template.recipient === null;
  }

  @action
  insertLiquid([fieldName]: [FieldName, number], liquid: string, fallback?: string) {
    let fieldNameToSelector = {
      'template.to': '.js-editor-field-to input',
      'template.body': '.js-editor-textarea',
    };
    let selector = fieldNameToSelector[fieldName];

    if (selector == null) {
      return;
    }

    if (this.container == null) {
      return;
    }

    let input = this.container.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement;
    if (isNone(input.selectionStart) || isNone(input.selectionEnd)) {
      return;
    }

    let { value, selectionStart, selectionEnd } = input;
    let beginStr = value.substring(0, selectionStart);
    let endStr = value.substring(selectionEnd, value.length);
    let liquidToInsert = getLiquidToInsert(liquid, fallback, this.template.templateEngine);

    input.value = `${beginStr}${liquidToInsert}${endStr}`;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  getChannelsAndUsers = task({ drop: true }, async () => {
    let result = await this.api.post('autocomplete/slack');

    let channels = result.channels.map((channel: string) => `#${channel}`);
    let users = result.users.map((user: string) => `@${user}`);

    this.channels = channels;
    this.users = users;
  });

  @action
  changeRecipient() {
    this.template.set('recipient', '');

    afterRender(() => {
      if (this.container == null) {
        return;
      }
      (this.container.querySelector('.js-editor-field-to .fly-form-control') as HTMLInputElement)?.focus();
    });
  }

  @action
  useDefaultRecipient() {
    // For webhook-triggered campaigns, we need to explicitly set the default
    // recipient, because null won't be recognized - when sending a message, we
    // don't access the campaign so we don't have the right context
    if (this.args.editableEntity.campaign?.isWebhookTriggered) {
      this.template.set('recipient', this.defaultRecipient);

      return;
    }

    this.template.set('recipient', null);
  }
}
